/* eslint-disable no-param-reassign */
import i18n from '@i18next/i18n';
import { showExportImage } from '@src/js/common/ark-extensions/dc-components/docs-component-export-image';
import * as mobx from 'mobx';
import ua from '@tencent/docs-user-agent';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import appConfigInstance from '@src/slide/infrastructure/module/dependent/config/tob/app-config';
import { ToolbarFileMenuConfig } from '@src/slide/infrastructure/module/dependent/config/config-manager-constants';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import SnackbarMobile from '@tencent/dui-mobile/lib/components/Snackbar';
import SnackbarPC from '@tencent/dui/lib/components/Snackbar';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { MobileUIState } from '@src/slide/infrastructure/module/independent/mobile/mobile-ui-state';
import { IBaseCopyPaste } from '@src/slide/application/service/copy-paste/core/base/base-copy-paste.interface';
import { IWatermarkManager } from '@src/slide/application/service/watermark-manager/core/interface';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import { UIState } from '@src/slide/infrastructure/module/independent/ui-state';
import {
    ISlideRenderStateManager, IStatePointEnum,
} from '@src/slide/application/service/slide-render-state/slide-render-state-manager.interface';

export interface ICustomOptionsProps {
    onClose?();
    [propName: string]: any;
}

interface IExportImageCommonProps extends ICustomOptionsProps {
    pagesCount?: number;
    onFetchPage?(pageNum: any, resolution: any): Promise<void>;
}

interface IAddWatermaskOptionProps extends IExportImageCommonProps {
    docWatermark?: any;
}

export default class ExportImageManager {
    /**
     * 是否处于生成图片中
     */
    @mobx.observable
    public isImageCopying = false;
    public copyPasteManager?: IBaseCopyPaste;
    public selectionService: ISelectionService;
    public uiDataService: IUiDataService;
    private watermarkManager: IDynamicService<IWatermarkManager>;
    private clearSlideReaction: () => void;

    public constructor(private slideRenderStateManager: ISlideRenderStateManager) {
        slideRenderStateManager.getStatePoints()[IStatePointEnum.visibleSlideRender].getPointCompletePromise()
            .then(() => {
                import('@tencent/docs-titlebar-service').then((module) => {
                    module.componentPropsBridge.setProps({
                        shareConfig: {
                            showExportImage: async (options) => {
                                const appShare = ua.isTencentDocsApp;
                                await this.exportImage({ ...options, appShare });
                            },
                        },
                    });
                });
            });
    }

    public setCopyPasteManager(copyPasteManager: IBaseCopyPaste) {
        this.copyPasteManager = copyPasteManager;
    }

    public setSelectionService(selectionService: ISelectionService) {
        this.selectionService = selectionService;
    }

    public setUiDataService(uiDataService: IUiDataService) {
        this.uiDataService = uiDataService;
    }

    public setWatermarkManager(watermarkManager: IDynamicService<IWatermarkManager>) {
        this.watermarkManager = watermarkManager;
    }


    public exportImage = async (options = {}) => {
        if (ua.isMobile) {
            if (!SlideDataServices.ContentStatusManager()?.isDocumentReady()) {
                this.showLoadingTips();
                return;
            }
            return await this.exportImageMobile(options);
        }
        return await this.exportImagePC(options);
    };

    public prepareExportImage = async (options = {}) => {
        if (!ua.isMobile) {
            return;
        }
        return await this.exportImageMobile({
            isLoadingExportImage: true,
            isMobile: true,
            ...options,
        });
    };

    public getExportImageMobileOptions = (customOptions: ICustomOptionsProps = {}) => {
        const { onClose, ...otherOptions } = customOptions;
        const exportImageOptions = {
            // 并发数量
            concurrency: 3,
            onClose: () => {
                onClose?.();
                this.isImageCopying = false;
            },
            isLocal: ENV.IS_MOBILE_LOCAL_EDIT,
            ...otherOptions,
        };
        const finalOptions = this.getExportImageCommonOptions(exportImageOptions);
        return finalOptions;
    };

    public getExportImagePCOptions = (customOptions: ICustomOptionsProps = {}) => {
        const { onClose, ...otherOptions } = customOptions;
        const exportImageOptions = {
            // 并发数量
            concurrency: 1,
            resolution: 1,
            onClose: () => {
                onClose?.();
            },
            ...otherOptions,
        };
        const finalOptions = this.getExportImageCommonOptions(exportImageOptions);
        return finalOptions;
    };

    private addWatermaskOption = async (options: IAddWatermaskOptionProps = {}) => {
        const watermarkManager = await this.watermarkManager.promise();
        const hasWaterMark = watermarkManager.watermarkInfo.show;
        if (hasWaterMark) {
            const { type: variant, text: content } = watermarkManager.watermarkInfo;
            options.docWatermark = Object.assign({
                variant,
                content,
                placement: 'tile',
                size: watermarkManager.getWaterMarkSizeForExportImage(),
            }, watermarkManager.isShowGuestWaterMark() ? { content2: watermarkManager.nickName } : {});
        }
    };

    private createSlideReaction = (instance) => {
        // 如果slide页数发生改变，更新组件
        const slideCountReaction = mobx.reaction(
            () => this.uiDataService.slideModelList.length,
            (pagesCount) => {
                instance.set({ pagesCount });
            },
        );
        const slideChangeReaction = SlideDataServices.SDKDataServices.EventManager()
            ?.mutationEvents.onCommitCommand(() => instance.invalidate());
        // 收到协同信息后，长图缓存失效
        return () => {
            slideCountReaction();
            slideChangeReaction?.();
        };
    };

    private getExportImageCommonOptions = (customOptions: IExportImageCommonProps = {}) => {
        const { onFetchPage, onClose, ...otherOptions } = customOptions;
        // 过滤带有隐藏属性的幻灯片
        let showingSlides: ISlideModel[] = this.uiDataService.presentation.children.filter(slide => slide.isShowInPlay);
        if (showingSlides.length === 0) {
            // 全部隐藏，默认视为全部不隐藏
            showingSlides = this.uiDataService.presentation.children;
        }
        const finalOptions = {
            pagesCount: showingSlides.length,
            onFetchPage: async (pageNum, resolution) => {
                await onFetchPage?.(pageNum, resolution);
                const slide = showingSlides[pageNum - 1];
                return this.copyPasteManager?.getSlideDataToCanvas([slide], resolution);
            },
            onClose: () => {
                onClose?.();
                // 关闭生成图片组件时加载底部栏
                UIState.isExportingToImage = false;
                this.clearSlideReaction?.();
            },
            watermarkSecurityCheck: !ENV.IS_LOCAL_EDIT,
            ...otherOptions,
            ...this.getConfigExtraOpt(),
        };
        return finalOptions;
    };

    private exportImageCommon = async (options: IExportImageCommonProps = {}) => {
        UIState.isExportingToImage = true;
        this.addWatermaskOption(options);
        const instance = await showExportImage(options);
        this.clearSlideReaction = this.createSlideReaction(instance);
        return instance;
    };

    @mobx.action
    private exportImageMobile = async (customOptions: ICustomOptionsProps = {}) => {
        this.isImageCopying = true;
        const options = this.getExportImageMobileOptions(customOptions);
        return await this.exportImageCommon(options);
    };

    private exportImagePC = async (customOptions: ICustomOptionsProps = {}) => {
        const options = this.getExportImagePCOptions(customOptions);
        return await this.exportImageCommon(options);
    };

    /**
     * 通过配置，是否屏蔽生成长图水印和vip功能
     */
    private getConfigExtraOpt = () => {
        const exportImgVip = appConfigInstance.getConfig(ToolbarFileMenuConfig.exportImgVip as any) ?? true;
        let disableVipValidation = false;
        let disableWatermarkContentInput = false;
        if (!exportImgVip) {
            disableVipValidation = true;
            disableWatermarkContentInput = true;
        }
        return { disableVipValidation, disableWatermarkContentInput };
    };

    /**
     * 为了避免画面resize抖动，新增假的currentSlide
     */
    private async addFakeCurrentSlide() {
        if (!this.copyPasteManager || !this.selectionService?.currentSlide) return;
        this.removeFakeCurrentSlide();
        const fakeCanvas = await this.copyPasteManager
            .getSlideDataToCanvas(
                [this.selectionService?.currentSlide],
                1,
                this.copyPasteManager.getCopyCanVasOptions(),
            );
        const fakeUrl = await fakeCanvas.toDataURL();
        const fakeModal = document.createElement('div');
        const fakeImage = document.createElement('img');
        fakeModal.setAttribute('id', 'export-image-fake-modal');
        fakeImage.setAttribute('src', fakeUrl);
        fakeModal.append(fakeImage);
        const containerDom = document.getElementById('select-view');
        containerDom?.insertBefore(fakeModal, containerDom?.firstElementChild);
    }

    private removeFakeCurrentSlide() {
        const fakeModal = document.getElementById('export-image-fake-modal');
        fakeModal?.remove();
    }

    private showLoadingTips() {
        const Snackbar = ua.isMobile ? SnackbarMobile : SnackbarPC;
        Snackbar.show({
            message: i18n.t('数据正在加载中，请稍侯'),
            autoClose: true,
            duration: 3000,
        });
    }
}
